/**
 * FG Search - default
 *
 */
(() => {
  const MODAL_ID = 'search-modal';

  /* ------------------------------------------------------------------ *
   * 1. Title truncation
   * ------------------------------------------------------------------ */

  const TITLE_SELECTOR = '.predictive-search-results__card--product .resource-card__title';
  const DESKTOP_MQ = '(min-width: 1024px)';
  const DESKTOP_LIMIT = 35;
  const MOBILE_LIMIT = 55;
  const FULL_TITLE_KEY = 'fgFullTitle';

  const desktopMql = window.matchMedia(DESKTOP_MQ);

  const getTitleLimit = () => (desktopMql.matches ? DESKTOP_LIMIT : MOBILE_LIMIT);

  const truncate = (str, limit) => {
    const s = String(str || '').trim();
    if (s.length <= limit) return s;
    // Reserve 3 chars for the ellipsis; strip trailing spaces so we don't get "word ..."
    return s.slice(0, Math.max(0, limit - 3)).replace(/\s+$/, '') + '...';
  };

  const applyTitle = (el) => {
    if (!(el instanceof HTMLElement)) return;
    if (!el.dataset[FULL_TITLE_KEY]) {
      el.dataset[FULL_TITLE_KEY] = el.textContent?.trim() ?? '';
    }
    const full = el.dataset[FULL_TITLE_KEY];
    const next = truncate(full, getTitleLimit());
    if (el.textContent !== next) el.textContent = next;
    if (full.length > getTitleLimit()) {
      if (el.getAttribute('aria-label') !== full) el.setAttribute('aria-label', full);
      if (el.getAttribute('title') !== full) el.setAttribute('title', full);
    } else {
      el.removeAttribute('aria-label');
      el.removeAttribute('title');
    }
  };

  const applyTitles = (root) => {
    const scope = root instanceof Element ? root : document;
    scope.querySelectorAll?.(TITLE_SELECTOR).forEach(applyTitle);
  };

  /* ------------------------------------------------------------------ *
   * 2. CTA copy
   * ------------------------------------------------------------------ */

  const BUTTON_SELECTOR = '.predictive-search__search-button';
  const INPUT_SELECTOR = 'input.search-input, input[name="q"]';

  const ctaTemplates = Object.assign(
    { withQuery: 'Search for "{query}"', empty: 'Search' },
    /** @type {{ fgSearch?: { cta?: { withQuery?: string; empty?: string } } }} */ (window)
      .fgSearch?.cta || {}
  );

  const formatCtaLabel = (query) => {
    const q = String(query || '').trim();
    return q ? ctaTemplates.withQuery.replace('{query}', q) : ctaTemplates.empty;
  };

  const syncCta = (modal) => {
    if (!(modal instanceof HTMLElement)) return;
    const button = modal.querySelector(BUTTON_SELECTOR);
    const input = modal.querySelector(INPUT_SELECTOR);
    if (!button || !input) return;
    const next = formatCtaLabel(input.value);
    if (button.textContent !== next) button.textContent = next;
  };

  /* ------------------------------------------------------------------ *
   * Wiring
   * ------------------------------------------------------------------ */

  const getModal = () => document.getElementById(MODAL_ID);

  const refresh = (modal) => {
    if (!modal) return;
    applyTitles(modal);
    syncCta(modal);
  };

  refresh(getModal());

  const modal = getModal();
  if (modal) {
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;
          if (node.matches?.(TITLE_SELECTOR)) applyTitle(node);
          else applyTitles(node);
        }
      }
      syncCta(modal);
    });
    observer.observe(modal, { childList: true, subtree: true });
  }

  // CTA: update as the user types.
  document.addEventListener(
    'input',
    (e) => {
      const t = e.target;
      if (!(t instanceof HTMLInputElement)) return;
      if (!t.classList.contains('search-input') && t.name !== 'q') return;
      const m = t.closest(`#${MODAL_ID}`) || getModal();
      if (m) syncCta(m);
    },
    true
  );

  document.addEventListener(
    'dialog:open',
    (e) => {
      const m = e.target;
      if (!(m instanceof HTMLElement) || m.id !== MODAL_ID) return;
      requestAnimationFrame(() => refresh(m));
    },
    true
  );

  desktopMql.addEventListener('change', () => applyTitles(getModal()));

  // Results page
  const SEARCH_ICON_SELECTOR = 'search-page-input-component .search__icon';

  /** @param {Element} el */
  const submitSearchForm = (el) => {
    const form = el.closest('form');
    if (!form) return;
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.submit();
  };

  document.querySelectorAll(SEARCH_ICON_SELECTOR).forEach((icon) => {
    icon.setAttribute('role', 'button');
    icon.setAttribute('tabindex', '0');
    icon.setAttribute('aria-label', 'Submit search');
  });

  document.addEventListener(
    'click',
    (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      const icon = target.closest(SEARCH_ICON_SELECTOR);
      if (icon) submitSearchForm(icon);
    },
    true
  );

  document.addEventListener(
    'keydown',
    (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const target = e.target;
      if (!(target instanceof Element)) return;
      if (!target.matches(SEARCH_ICON_SELECTOR)) return;
      e.preventDefault();
      submitSearchForm(target);
    },
    true
  );
})();
